import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import * as JwtDecode from 'jwt-decode';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiUrlService } from '../apiUrl/api-url.service';
import { UserStateService } from '../user/user-state.service';

interface AuthPayload {
    user: string;
    employeeCode: string;
    assignmentId: string;
    exp: number;
    [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private userSubject = new BehaviorSubject<AuthPayload | null>(null);
    public user$ = this.userSubject.asObservable();

    private authLogin: string;
    private authLogout: string;
    private authRefresh: string;
    private tokenRefreshInterval: any;

    // Inactividad
    private inactivityTimeLimit = 5 * 60 * 1000; // 5 minutos
    private responseTimeLimit = 30 * 1000; // 30 segundos para responder
    private inactivityTimer: any;
    private modalResponseTimer: any;
    private isTimeoutModalActive = false;
    private modalEndTime!: number;

    private showTimeoutModalSubject = new BehaviorSubject<boolean>(false);
    public showTimeoutModal$ = this.showTimeoutModalSubject.asObservable();

    // Progreso 30 segundos
    private responseProgressSubject = new BehaviorSubject<number>(0);
    public responseProgress$ = this.responseProgressSubject.asObservable();

    //token revocado
    private showRevokedTokenModalSubject = new BehaviorSubject<boolean>(false);
    public showRevokedTokenModal$ = this.showRevokedTokenModalSubject.asObservable();


    constructor(
        private http: HttpClient,
        private apiUrlService: ApiUrlService,
        private cookieService: CookieService,
        private router: Router,
        private userStateService: UserStateService
    ) {
        this.authLogin = this.apiUrlService.apiAuthLogin();
        this.authLogout = this.apiUrlService.apiAuthlogout();
        this.initializeSession();
        this.setupTokenRefresh();
    }

    login(credentials: { username: string; password: string }): Observable<any> {
        return this.http
            .post(this.authLogin, credentials, {
                observe: 'response',
                withCredentials: true,
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            })
            .pipe(
                tap(() => {
                    this.setUserFromToken();
                    this.setupTokenRefresh();
                })
            );
    }

    handleSamlAuthSuccess(token: string, refreshToken: string): void {
        this.cookieService.set('token', token, {
            path: '/',
            domain: '.claro.com.gt',
            secure: true,
            sameSite: 'None',
        });
        this.cookieService.set('refresh_token', refreshToken, {
            path: '/',
            domain: '.claro.com.gt',
            secure: true,
            sameSite: 'None',
        });

        this.setUserFromToken();
    }

    isAuthenticated(): boolean {
        const token = this.getToken();
        if (!token) return false;
        try {
            const payload: any = JwtDecode.jwtDecode(token);
            return payload.exp * 1000 > Date.now();
        } catch {
            return false;
        }
    }

    getTokenPayload(): AuthPayload | null {
        const token = this.getToken();
        if (!token) return null;
        try {
            return JwtDecode.jwtDecode(token);
        } catch {
            return null;
        }
    }

    private setupTokenRefresh(): void {
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
        }

        this.tokenRefreshInterval = setInterval(() => {
            if (this.shouldRefreshToken()) {
                this.refreshToken().subscribe({
                    error: () => this.clearSession(),
                });
            }
        }, 300_000); // cada 5 minutos
    }

    private shouldRefreshToken(): boolean {
        const payload = this.getTokenPayload();
        if (!payload) return false;
        return payload.exp * 1000 - Date.now() < 30 * 60 * 1000; // menos de 30 minutos
    }

    refreshToken(): Observable<any> {
        return this.http
            .post(this.authRefresh, {}, { withCredentials: true })
            .pipe(
                tap(() => {
                    setTimeout(() => {
                        this.setUserFromToken();
                    }, 100);
                })
            );
    }

    setUserFromToken(): void {
        const payload = this.getTokenPayload();
        if (payload) {
            // Guardar en memoria (no en localStorage) para evitar almacenamiento en texto claro
            this.userStateService.employeeCode = payload.employeeCode;
            this.userStateService.assignmentId = payload.assignmentId;
            this.userSubject.next(payload);
        } else {
            this.clearSession();
        }
    }

    getToken(): string | null {
        return this.cookieService.get('token') || null;
    }

    logout(): void {
        this.http.post(this.authLogout, {}, { withCredentials: true }).subscribe({
            next: () => this.clearSession(),
            error: (err) => {
                console.error('Error al cerrar sesión:', err);
                this.clearSession();
            },
        });
    }

    clearSession(): void {
        const cookies = ['token', 'refresh_token', 'csrfToken'];
        cookies.forEach(cookie => {
            this.cookieService.delete(cookie, '/', '.claro.com.gt');
            this.cookieService.delete(cookie, '/');
        });
        // No borrar todo localStorage (evitar afectar otros apps); limpiar solo estado en memoria
        this.userStateService.clear();
        this.userSubject.next(null);
        clearInterval(this.tokenRefreshInterval);
        this.router.navigate(['/']);
    }

    initializeSession(): void {
        const token = this.getToken();

        if (token && this.isAuthenticated()) {
            this.setUserFromToken();
        } else {
            this.userSubject.next(null);
        }
    }

    getTokenExpirationTime(): string {
        const payload = this.getTokenPayload();
        if (!payload?.exp) return 'Token inválido';
        const timeLeft = payload.exp * 1000 - Date.now();
        if (timeLeft <= 0) return 'El token ha expirado';
        const minutes = Math.floor(timeLeft / 60000);
        const seconds = Math.floor((timeLeft % 60000) / 1000);
        return `Expira en ${minutes} min ${seconds} seg`;
    }

    getUserEmail(): string | null {
        return this.userSubject.value?.user || null;
    }

    getEmployeeCode(): string | null {
        // Preferir estado en memoria; si no existe, intentar leer del token (no recomendable para almacenamiento)
        const mem = this.userStateService.employeeCode;
        if (mem) return mem;
        const payload = this.getTokenPayload();
        return payload?.employeeCode ?? null;
    }

    getAssignmentId(): string | null {
        const mem = this.userStateService.assignmentId;
        if (mem) return mem;
        const payload = this.getTokenPayload();
        return payload?.assignmentId ?? null;
    }

    /** Inicia monitoreo de inactividad */
    startInactivityWatcher(): void {
        this.resetInactivityTimer();
        ['mousemove', 'keydown', 'click'].forEach(event =>
            window.addEventListener(event, this.resetInactivityTimer.bind(this))
        );
    }

    /** Reinicia el temporizador de inactividad */
    private resetInactivityTimer(): void {
        if (this.isTimeoutModalActive) return;

        clearTimeout(this.inactivityTimer);
        clearInterval(this.modalResponseTimer);
        this.responseProgressSubject.next(0);

        this.inactivityTimer = setTimeout(() => {
            this.isTimeoutModalActive = true;
            this.showTimeoutModalSubject.next(true);

            const totalMs = this.responseTimeLimit;
            this.modalEndTime = Date.now() + totalMs; // momento exacto en que expira

            this.modalResponseTimer = setInterval(() => {
                const remaining = this.modalEndTime - Date.now(); //tiempo real que queda
                const progress = Math.min(100, ((totalMs - remaining) / totalMs) * 100);
                this.responseProgressSubject.next(progress);

                if (remaining <= 0) {
                    clearInterval(this.modalResponseTimer);
                    this.respondToInactivity(false);
                }
            }, 100);
        }, this.inactivityTimeLimit);
    }

    /** Usuario responde al modal de inactividad */
    respondToInactivity(continueSession: boolean): void {
        clearInterval(this.modalResponseTimer);
        this.responseProgressSubject.next(0);
        this.isTimeoutModalActive = false;
        this.showTimeoutModalSubject.next(false);

        if (continueSession) {
            this.resetInactivityTimer();
        } else {
            this.logout();
        }
    }

    triggerRevokedTokenModal(): void {
        this.showRevokedTokenModalSubject.next(true);
    }

    hideRevokedTokenModal(): void {
        this.showRevokedTokenModalSubject.next(false);
    }

}
